// A car's overview: its TUNE MFD, its pages, its trip computer in its own units, what it is (which its MFD shows),
// what the Analyser uses (which the MFD doesn't), anything else signed in in it, and deleting it.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METRIC, type components, type Units } from '@tune/shared';
import { Fragment } from 'react';
import { useNavigate } from 'react-router';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { distanceText, fuelText, grouped, serviceText, tripParts } from '../../lib/car';
import { deviceName, isMfd } from '../../lib/words';
import { useCarsChanged, useDevices, useUnits } from '../../queries';
import {
  Banner,
  Button,
  ButtonLink,
  Card,
  ErrorBanner,
  ListButton,
  ListSection,
  ListValue,
  Skeleton,
  useConfirm,
  useRouteSheet,
  useToast,
} from '../../ui';
import { QrCodeIcon, TrashIcon } from '../../ui/icons';
import { CarPage } from '../car/CarPage';
import s from './Car.module.css';
import { CarRows } from './CarNav';
import { EditAnalyserSheet, EditCarSheet } from './CarSheets';
import { MfdCard, SeenLine, useSignOutDevice } from './Mfd';

type Vehicle = components['schemas']['Vehicle'];
type Device = components['schemas']['Device'];

export function Car() {
  return <CarPage>{(car) => <Overview car={car} />}</CarPage>;
}

function Overview({ car }: { car: Vehicle }) {
  const units = useUnits(car.id) ?? METRIC;
  const [editing, setEditing] = useRouteSheet('edit-car');
  const [figures, setFigures] = useRouteSheet('edit-analyser');
  return (
    <>
      <div className={s.columns}>
        <div className={s.column}>
          <MfdSection car={car} />
          <CarRows car={car} />
          <TripComputer car={car} units={units} />
          <OtherSignIns car={car} />
        </div>
        <div className={s.column}>
          <About car={car} onEdit={() => setEditing(true)} />
          <ForTheAnalyser car={car} onEdit={() => setFigures(true)} />
          <DeleteCar car={car} />
        </div>
      </div>
      <EditCarSheet car={car} open={editing} onClose={() => setEditing(false)} />
      <EditAnalyserSheet car={car} open={figures} onClose={() => setFigures(false)} />
    </>
  );
}

function MfdSection({ car }: { car: Vehicle }) {
  const devices = useDevices();
  if (!car.mfd) {
    return (
      <Card title="TUNE MFD">
        <p className={s.text}>
          No MFD in this car. To sign one in, open Settings on the MFD, then TUNE account, and scan its code with your
          phone's camera.
        </p>
        <div>
          <ButtonLink to="/pair" variant="tinted" size="small" icon={<QrCodeIcon size={16} weight="bold" />}>
            Sign in an MFD
          </ButtonLink>
        </div>
      </Card>
    );
  }
  const device = devices.data?.devices.find((d) => d.id === car.mfd?.id);
  if (!device) {
    // Its devices are on their way: what the car says of its MFD meanwhile.
    return (
      <Card title={car.mfd.name || 'TUNE MFD'} subtitle={<SeenLine device={car.mfd} />}>
        {devices.isError && (
          <ErrorBanner retry={() => void devices.refetch()}>The MFD's details couldn't load.</ErrorBanner>
        )}
        {devices.isPending && <Skeleton lines={2} label="Loading the MFD" />}
      </Card>
    );
  }
  return <MfdCard device={device} car={car} sheet="move-mfd" />;
}

function TripComputer({ car, units }: { car: Vehicle; units: Units }) {
  const c = car.counters;
  const service = serviceText(car.serviceDue, units);
  if (!c) {
    return (
      <ListSection
        header="Trip computer"
        footer="Its odometer, trips and engine hours come from the MFD as it's driven."
      >
        <ListValue label="Nothing from the MFD yet" />
      </ListSection>
    );
  }
  return (
    <ListSection header="Trip computer" footer="As the MFD counts them, not the car's own odometer.">
      <ListValue
        label="Odometer"
        subtitle={`${grouped(c.engineHours, 1)} h of engine time`}
        detail={distanceText(units, c.odometerKm)}
      />
      <ListValue
        label="Trip A"
        subtitle={<Figures parts={tripParts(units, c.a)} />}
        detail={distanceText(units, c.a.distanceKm)}
      />
      <ListValue
        label="Trip B"
        subtitle={<Figures parts={tripParts(units, c.b)} />}
        detail={distanceText(units, c.b.distanceKm)}
      />
      <ListValue
        label="Since the last service"
        subtitle={`${grouped(Math.max(0, c.engineHours - c.serviceHours), 1)} h of engine time`}
        detail={distanceText(units, Math.max(0, c.odometerKm - c.serviceKm))}
      />
      {service && (
        <ListValue
          label="Next service"
          detail={<span className={service.overdue ? s.warn : undefined}>{service.text}</span>}
        />
      )}
    </ListSection>
  );
}

/** Figures in a line, each kept whole: "184 km/h" never breaks before its unit. */
function Figures({ parts }: { parts: string[] }) {
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={part}>
          {i > 0 && ' · '}
          <span className={s.whole}>{part}</span>
        </Fragment>
      ))}
    </>
  );
}

function OtherSignIns({ car }: { car: Vehicle }) {
  const devices = useDevices();
  const others = (devices.data?.devices ?? []).filter((d) => d.vehicleId === car.id && !isMfd(d));
  if (others.length === 0) {
    return null;
  }
  return (
    <ListSection header="Other sign-ins" footer="Other devices signed in to your account, in this car.">
      {others.map((d) => (
        <OtherSignIn key={d.id} device={d} />
      ))}
    </ListSection>
  );
}

function OtherSignIn({ device }: { device: Device }) {
  const signOut = useSignOutDevice(device);
  const name = deviceName(device);
  return (
    <ListValue
      label={name}
      subtitle={<SeenLine device={device} />}
      detail={
        <Button variant="destructivePlain" size="small" busy={signOut.pending} onClick={() => void signOut.ask()}>
          Sign out<span className="visually-hidden"> {name}</span>
        </Button>
      }
    />
  );
}

function About({ car, onEdit }: { car: Vehicle; onEdit: () => void }) {
  const rows: Array<readonly [string, string | null]> = [
    ['Make', car.make],
    ['Model', car.model],
    ['Year', car.year === null ? null : String(car.year)],
    ['Engine', car.engine],
    ['Transmission', car.transmission],
  ];
  // Only what's given: an empty row says nothing.
  const given = rows.filter((row): row is readonly [string, string] => row[1] !== null && row[1] !== '');
  return (
    <ListSection
      header="About the car"
      headerAction={
        <Button variant="plain" size="small" onClick={onEdit}>
          Edit
        </Button>
      }
      footer="Its TUNE MFD shows these on its TUNE account page."
    >
      <ListValue label="Name" detail={car.nickname} />
      {given.map(([label, value]) => (
        <ListValue key={label} label={label} detail={value} />
      ))}
      {car.bio && <ListValue label="About it" subtitle={car.bio} />}
      {given.length === 0 && !car.bio && (
        <ListButton tone="accent" label="Add its make, model and year" onClick={onEdit} />
      )}
    </ListSection>
  );
}

function figure(n: number | null, places: number, unit = ''): string {
  return n === null ? 'Not given' : `${n.toLocaleString('en-AU', { maximumFractionDigits: places })}${unit}`;
}

function ForTheAnalyser({ car, onEdit }: { car: Vehicle; onEdit: () => void }) {
  const none = car.fuel === null && car.weightKg === null && car.dragCoefficient === null && car.frontalAreaM2 === null;
  return (
    <ListSection
      header="For the Analyser"
      headerAction={
        <Button variant="plain" size="small" onClick={onEdit}>
          Edit
        </Button>
      }
      footer="The Analyser's dyno and fuelling checks use these, and a typical car's figures for any not given. The MFD doesn't."
    >
      {none ? (
        <ListButton tone="accent" label="Add its weight, fuel and more" onClick={onEdit} />
      ) : (
        [
          <ListValue key="fuel" label="Fuel" detail={fuelText(car.fuel) ?? 'Not given'} />,
          <ListValue key="weight" label="Weight" detail={figure(car.weightKg, 0, ' kg')} />,
          <ListValue key="cd" label="Drag coefficient" detail={figure(car.dragCoefficient, 3)} />,
          <ListValue key="area" label="Frontal area" detail={figure(car.frontalAreaM2, 2, ' m²')} />,
        ]
      )}
    </ListSection>
  );
}

function DeleteCar({ car }: { car: Vehicle }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const changed = useCarsChanged();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const say = useToast();
  const remove = useMutation({
    mutationFn: () => data(api.DELETE('/vehicles/{vehicleId}', { params: { path: { vehicleId: car.id } } })),
    onSuccess: async () => {
      void navigate('/garage', { replace: true });
      // Everything read for it goes: its settings, datalogs and records too.
      queryClient.removeQueries({ predicate: (q) => q.queryKey.includes(car.id) });
      await changed();
      say(`${car.nickname} is deleted.`);
    },
  });
  const ask = async () => {
    const yes = await confirm({
      title: `Delete ${car.nickname}?`,
      message:
        "Its settings, service history, trips and datalogs go for good. Its TUNE MFD stays signed in, and asks which car it's in.",
      confirm: 'Delete car',
    });
    if (yes) {
      remove.mutate();
    }
  };
  return (
    <>
      {remove.isError && <Banner tone="error">{problem(remove.error)}</Banner>}
      <ListSection footer="Its settings, service history, trips and datalogs go with it.">
        <ListButton
          tone="destructive"
          icon={<TrashIcon size={20} weight="bold" />}
          label="Delete car"
          disabled={remove.isPending}
          onClick={() => void ask()}
        />
      </ListSection>
    </>
  );
}
